"use client"
import Text from "@/app/(site)/_components/Atoms/Text"
import BulletList from "@/app/(site)/_components/Molecules/BulletList"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { useProductCardContext } from "@/app/(site)/_components/Organisms/Cards/ProductCards/ProductCard/ProductCardContext"
import ProductCardSwatches from "@/app/(site)/_components/Organisms/Cards/ProductCards/ProductCard/ProductCardSwatches"
import { type RelatedVariant } from "@/lib/content-service/types/ContentServiceProduct"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { FOCUS_VISIBLE_INSET_CLASS } from "@/src/utils/a11y/focusVisible"
import { t } from "@/translations"

type ProductCardDetailsProps = {
    sortedSwatches: RelatedVariant[]
    langCode: string
    uri?: string
    variantLinkLabel: string
    name: string
    price?: string
    priceBeforeDiscount: string
    showAsOnSale: boolean
    productBenefits?: string[]
    available: boolean
    showBenefits?: boolean
    listName?: string
    listId?: string
    listType?: string
    listHierarchy?: string
}

export default function ProductCardDetails({
    sortedSwatches,
    langCode,
    uri,
    variantLinkLabel,
    name,
    price,
    priceBeforeDiscount,
    showAsOnSale,
    productBenefits,
    available,
    showBenefits,
    listName,
    listId,
    listType,
    listHierarchy
}: ProductCardDetailsProps) {
    const siteContext = useSiteContext()
    const { handleChangeVariant } = useProductCardContext()

    // Helper function to track color swatch clicks properly
    const handleGoToSwatchTracking = (swatch: RelatedVariant, swatchIndex: number) => {
        const trackingData: RelatedVariant = {
            ...swatch,
            // Ensure we have the required properties for tracking
            sku: swatch.sku || swatch.product || "unknown",
            name: swatch.name || "Unknown",
            priceAsNumber: swatch.priceAsNumber || 0,
            price: swatch.price || "",
            available: swatch.available || false,
            color_swatch: swatch.color_swatch || undefined
        }

        import("@/lib/utils/tracking/gtmEvents").then(({ selectItemEvent }) => {
            selectItemEvent(
                trackingData,
                { size: undefined, index: swatchIndex },
                listName || "Product List",
                listId || "plp",
                siteContext,
                listType || "plp",
                listHierarchy
            )
        })
    }

    return (
        <div
            onMouseLeave={() => handleChangeVariant(undefined)}
            className="grid grid-cols-12 overflow-hidden"
        >
            <div className={"col-span-12 lg:col-span-6"}>
                <DynamicLink
                    href={`/product/${uri}`}
                    prefetch={false}
                    aria-label={variantLinkLabel}
                    className={FOCUS_VISIBLE_INSET_CLASS}
                >
                    <Text size="small" weight="regular" className="lg:text-16">
                        {name}
                    </Text>
                    <Text
                        size="small"
                        weight="regular"
                        className="mb-8 flex gap-8 text-main-dark-grey lg:text-16"
                    >
                        <span>{price}</span>
                        {showAsOnSale && (
                            <span className="line-through">{priceBeforeDiscount}</span>
                        )}
                    </Text>
                </DynamicLink>
                <ProductCardSwatches
                    sortedSwatches={sortedSwatches}
                    langCode={langCode}
                    handleChangeVariant={handleChangeVariant}
                    showBenefits={showBenefits}
                    handleGoToSwatchTracking={handleGoToSwatchTracking}
                />
            </div>
            <div className="col-span-2 mt-8 hidden lg:col-span-1 lg:flex">
                {available ? (
                    Array.isArray(productBenefits) &&
                    productBenefits.length > 0 &&
                    showBenefits && <BulletList title={productBenefits.slice(0, 3)} />
                ) : (
                    <Text
                        size="medium"
                        weight="regular"
                        className="!text-14 uppercase text-black lg:!text-16"
                    >
                        {t(langCode, "product_page.out_of_stock")}
                    </Text>
                )}
            </div>
        </div>
    )
}
